// src/features/inventory/pages/ItemDetailPage/TransferStockDialog.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { CompareArrows as TransferIcon } from '@mui/icons-material';
import { stockAPI } from '../../services/stock';
import { useApi } from '../../../../hooks/api/useApi';
import { useStockLocations } from '../../hooks/useStockLocations';
import { useLots } from '../../hooks/useLots';

const EMPTY_FORM = { lot_id: '', from_location_id: '', to_location_id: '', quantity: '', notes: '' };

// Sentinel used as MUI select value when the actual location_id is null ("ingen lokation")
const NULL_LOC = '__no_location__';
const toApiLoc = (val) => (val === NULL_LOC ? null : val);

export default function TransferStockDialog({ open, onClose, onSuccess, itemId, uom = '', lotTracked = false, locationBalances = [], preselectedLot = null }) {
  const { t } = useTranslation(['inventory', 'common']);
  const { loading, error, execute, clearError } = useApi();
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedLotDetail, setSelectedLotDetail] = useState(null);
  const { stockLocations, fetchStockLocations } = useStockLocations();
  const { lots, fetchLotsByItem, fetchLotById } = useLots();

  const effectiveLotTracked = lotTracked || !!preselectedLot;

  useEffect(() => {
    if (open) {
      if (preselectedLot) {
        setForm({ ...EMPTY_FORM, lot_id: preselectedLot.lot_id });
        setSelectedLotDetail(preselectedLot);
      } else {
        setForm(EMPTY_FORM);
        setSelectedLotDetail(null);
        if (effectiveLotTracked) fetchLotsByItem(itemId);
      }
      fetchStockLocations();
    }
  }, [open, fetchStockLocations, fetchLotsByItem, effectiveLotTracked, itemId, preselectedLot]);

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleLotChange = async (e) => {
    const lotId = e.target.value;
    setForm(prev => ({ ...prev, lot_id: lotId, from_location_id: '', quantity: '' }));
    setSelectedLotDetail(null);
    if (lotId) {
      const result = await fetchLotById(lotId);
      if (result.success) setSelectedLotDetail(result.data.data);
    }
  };

  // Source locations: lot-tracked uses the selected lot's per-location balances;
  // otherwise use the item-level location balances passed from the parent.
  const sourceLocations = effectiveLotTracked
    ? (selectedLotDetail?.location_balances ?? []).filter(b => b.quantity > 0)
    : locationBalances.filter(b => b.quantity > 0);

  // Use sentinel to find the balance for the currently selected from-location
  const fromBalance = sourceLocations.find(
    b => (b.location_id ?? NULL_LOC) === form.from_location_id
  );
  const available = fromBalance?.quantity ?? null;

  const activeLocations = stockLocations.filter(l => l.active);
  const toLocations = activeLocations.filter(l => l.stock_location_id !== form.from_location_id);
  // Show "ingen lokation" as a destination option unless it is already the source
  const showNoLocationDest = form.from_location_id !== NULL_LOC;

  // Balance lookup for destination options (lot-level when lot-tracked, item-level otherwise)
  const destBalances = effectiveLotTracked
    ? (selectedLotDetail?.location_balances ?? [])
    : locationBalances;
  const getDestQty = (locationId) =>
    destBalances.find(b => b.location_id === locationId)?.quantity ?? 0;

  const activeLots = lots.filter(l => l.status === 'active');

  const qty = form.quantity === '' ? 0 : parseInt(form.quantity, 10);
  const isValid =
    (!effectiveLotTracked || form.lot_id !== '') &&
    form.from_location_id !== '' &&
    form.to_location_id !== '' &&
    form.from_location_id !== form.to_location_id &&
    form.quantity !== '' &&
    !isNaN(qty) &&
    qty > 0 &&
    (available === null || qty <= available);

  const handleClose = () => {
    clearError();
    onClose();
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    const result = await execute(() =>
      stockAPI.transferStock({
        item_id: itemId,
        from_location_id: toApiLoc(form.from_location_id),
        to_location_id: toApiLoc(form.to_location_id),
        quantity: qty,
        lot_id: effectiveLotTracked ? form.lot_id : null,
        notes: form.notes.trim() || null,
      })
    );
    if (result.success) {
      onSuccess();
      handleClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('inventory:items.transfer_stock.dialog_title')}</DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          {error && <Alert severity="error" onClose={clearError}>{error}</Alert>}

          {effectiveLotTracked && !preselectedLot && (
            <TextField
              select
              label={t('inventory:items.transfer_stock.lot')}
              value={form.lot_id}
              onChange={handleLotChange}
              required
              disabled={loading}
            >
              {activeLots.length === 0 && (
                <MenuItem value="" disabled>
                  <Typography color="text.secondary" variant="body2">
                    {t('inventory:items.transfer_stock.no_lots')}
                  </Typography>
                </MenuItem>
              )}
              {activeLots.map((lot) => (
                <MenuItem key={lot.lot_id} value={lot.lot_id}>
                  {lot.lot_number}
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({lot.qty_on_hand} {uom})
                  </Typography>
                </MenuItem>
              ))}
            </TextField>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5 }}>
            <TextField
              select
              label={t('inventory:items.transfer_stock.from_location')}
              value={form.from_location_id}
              onChange={set('from_location_id')}
              required
              disabled={loading || (effectiveLotTracked && !selectedLotDetail)}
              sx={{ flex: '1 1 160px' }}
            >
              {sourceLocations.length === 0 && (
                <MenuItem value="" disabled>
                  <Typography color="text.secondary" variant="body2">
                    {t('inventory:items.transfer_stock.no_source_locations')}
                  </Typography>
                </MenuItem>
              )}
              {sourceLocations.map((b) => (
                <MenuItem key={b.location_id ?? NULL_LOC} value={b.location_id ?? NULL_LOC}>
                  {b.location_id == null ? t('inventory:items.no_location') : b.location_name}
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({b.quantity} {uom})
                  </Typography>
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label={t('inventory:items.transfer_stock.to_location')}
              value={form.to_location_id}
              onChange={set('to_location_id')}
              required
              disabled={loading || !form.from_location_id}
              sx={{ flex: '1 1 160px' }}
            >
              {toLocations.length === 0 && !showNoLocationDest && (
                <MenuItem value="" disabled>
                  <Typography color="text.secondary" variant="body2">
                    {t('inventory:items.transfer_stock.no_dest_locations')}
                  </Typography>
                </MenuItem>
              )}
              {showNoLocationDest && (
                <MenuItem value={NULL_LOC}>
                  {t('inventory:items.no_location')}
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({getDestQty(null)} {uom})
                  </Typography>
                </MenuItem>
              )}
              {toLocations.map((loc) => (
                <MenuItem key={loc.stock_location_id} value={loc.stock_location_id}>
                  {loc.name}
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({getDestQty(loc.stock_location_id)} {uom})
                  </Typography>
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <TextField
            label={t('inventory:items.transfer_stock.quantity')}
            type="number"
            value={form.quantity}
            onChange={set('quantity')}
            required
            disabled={loading}
            helperText={
              available !== null
                ? t('inventory:items.transfer_stock.available_hint', { qty: available, uom })
                : undefined
            }
            slotProps={{ htmlInput: { step: 1, min: 1, max: available ?? undefined } }}
          />

          <TextField
            label={t('inventory:items.transfer_stock.notes')}
            value={form.notes}
            onChange={set('notes')}
            multiline
            rows={2}
            disabled={loading}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t('common:buttons.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid || loading}
          startIcon={loading ? <CircularProgress size={16} /> : <TransferIcon />}
        >
          {loading ? t('common:buttons.saving') : t('inventory:items.transfer_stock.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
